import { inject, Injectable, OnDestroy } from '@angular/core';
import { app } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { NCore } from '../df-core.interfaces';

export namespace NPrivacyState {
    /**
     * Status of the privacy state checking
     */
    export enum Status {
        /**
         * Initial state
         */
        pending,
        /**
         * Modal is open or needed
         */
        modal,
        /**
         * State is saved or read from storage
         */
        ready
    }

    /**
     * Areas of cookies
     */
    export enum Area {
        core = 0,
        performance = 1,
        personalization = 2,
        advertising = 3
    }

    export type TSelection = Record<Area, boolean>;
}

/**
 * Service to manage the privacy state
 */
@Injectable({
    providedIn: 'root'
})
export class PrivacyStateService implements OnDestroy {
    /**
     * Informs about the status of the privacy state checking
     */
    readonly checkStatus$ = new BehaviorSubject<NPrivacyState.Status>(NPrivacyState.Status.pending);

    /**
     * Undefined until service is initialized (checkStatus$ is ready or modal is open)
     * after initialization it will emit the current selection
     */
    readonly selection$ = new BehaviorSubject<NPrivacyState.TSelection | undefined>(undefined);

    /**
     * Triggered on any change
     */
    private notify$ = new Subject<void>();

    /**
     * Selection map
     *
     * Please avoid direct manipulation and use setValue instead
     */
    private privacyStates$ = new Map<NPrivacyState.Area, BehaviorSubject<boolean>>();

    /**
     * Services subscriptions
     */
    private subscriptions = new Subscription();

    /**
     * Timeout service
     */
    private timeoutService = inject(TimeoutService);
    /**
     * Modules settings
     */
    private moduleSettings = inject(NCore.OPTIONS);

    /**
     * Constructor
     */
    constructor() {
        const active = this.moduleSettings.privacy.active;
        for (const area of Object.keys(active)) {
            if (active[area]) {
                this.getPrivacyState(area as any as NPrivacyState.Area);
            }
        }

        const cb = this.timeoutService.debounce(() => {
            const selection = this.checkStatus$.value !== NPrivacyState.Status.pending ? this.getSelection() : undefined;
            if (!isEqual(selection, this.selection$.value)) {
                this.selection$.next(selection);
            }
        }, 100);

        this.subscriptions.add(this.checkStatus$.subscribe(cb));
        this.subscriptions.add(this.notify$.subscribe(cb));

        app['privacyStateService'] = this;
    }

    /**
     * Destructor
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Sets the check status
     */
    setCheckStatus(status: NPrivacyState.Status) {
        if (this.checkStatus$.value !== status) {
            this.checkStatus$.next(status);
        }
    }

    /**
     * Sets the value of a privacy state
     */
    setValue(area: NPrivacyState.Area, value: boolean) {
        if (area === NPrivacyState.Area.core) {
            value = true;
        }
        const subject = this.getPrivacyState(area);
        if (subject.value !== value) {
            subject.next(value);
        }
    }

    /**
     * Retrieves the value of a privacy state
     */
    getValue(area: NPrivacyState.Area): boolean {
        return this.getState(area).value;
    }

    /**
     * Gets state subject
     */
    getState(area: NPrivacyState.Area): BehaviorSubject<boolean> {
        if (typeof area === 'number') {
            area = area.toString() as unknown as NPrivacyState.Area;
        }
        if (!this.privacyStates$.has(area)) {
            throw new Error(`Privacy state for area ${area} not found`);
        }
        return this.privacyStates$.get(area)!;
    }

    /**
     * Retrieves the areas of privacy states
     */
    getAreas() {
        return Array.from(this.privacyStates$.keys());
    }

    /**
     * Retrieves the current selection of privacy states
     */
    getSelection(): NPrivacyState.TSelection {
        const selection: NPrivacyState.TSelection = {} as NPrivacyState.TSelection;
        for (const [area, subject] of this.privacyStates$.entries()) {
            selection[area] = subject.value;
        }
        return selection;
    }

    /**
     * Retrieves or creates a privacy state subject/observable pair
     */
    private getPrivacyState(area: NPrivacyState.Area): BehaviorSubject<boolean> {
        if (!this.privacyStates$.has(area)) {
            const subject = new BehaviorSubject(false);
            this.privacyStates$.set(area, subject);
            // notify on any change
            this.subscriptions.add(subject.subscribe(() => this.notify$.next()));
        }
        return this.privacyStates$.get(area)!;
    }
}
